
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../utils/url.js";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const DetailedForm = ({ bookingData, onClose, locations, onBookingSuccess }) => {
  const now = new Date();
  const [errors, setErrors] = useState({});
  const [activeSection, setActiveSection] = useState("datetime");
  const [isAirportPickup, setIsAirportPickup] = useState(false);
  const [isAirportDestination, setIsAirportDestination] = useState(false);
  const [dataAir, setDataAir] = useState(null);
  const [isLoadingAirportData, setIsLoadingAirportData] = useState(false);

  const [formData, setFormData] = useState({
    sendhouseNo: "",
    sendstreetName: "",
    sendtown: "",
    sendpostCode: "",
    rechouseNo: "",
    recstreetName: "",
    rectown: "",
    recpostcode: "",
    title: "",
    name: "",
    email: "",
    phoneNumber: "",
    instructions: "",
    arrivalTime: "",
    flightNumber: "",
    mobileNumber: "",
    departureTime: "",
    destinationFlightNumber: "",
    terminal: "",
    streetName1: "",
    post_code: "",
    town: "",
  });

  const [locationFrom] = useState(bookingData?.locationFrom?.id?.toString() || "");
  const [locationTo] = useState(bookingData?.locationTo?.id?.toString() || "");
  const [dateTime, setDateTime] = useState(bookingData?.rawDateTime || now);
  const [pricedetail] = useState(bookingData?.priceDetails?.total?.toFixed(2) || 0);

  const fetchAirportDetails = async (locationName) => {
    if (!locationName) return null;
    
    try {
      setIsLoadingAirportData(true);
      const response = await fetch(`${BASE_URL}/api/location/getbyname/${encodeURIComponent(locationName)}`);
      const data = await response.json();
      
      if (response.ok) {
        setDataAir(data.data);
        
        // Auto-fill the form data with airport details
        setFormData(prev => ({
          ...prev,
          streetName1: data.data.streetName1 || data.data.streetName1 || "",
          post_code: data.data.post_code || data.data.post_code || "",
          town: data.data.town || data.data.town || ""
        }));
        
        return data.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching airport details:", error);
      return null;
    } finally {
      setIsLoadingAirportData(false);
    }
  };

  useEffect(() => {
    if (locationFrom) {
      const selectedLocation = locations.find(loc => loc.id.toString() === locationFrom);
      const locationName = selectedLocation?.name.toLowerCase();
      setIsAirportPickup(
        locationName.includes('airport') || 
        locationName.includes('heathrow')
      );
    } else {
      setIsAirportPickup(false);
    }

    if (locationTo) {
      const selectedLocation = locations.find(loc => loc.id.toString() === locationTo);
      const locationName = selectedLocation?.name;
      const isAirport = locationName?.toLowerCase().includes('airport') || 
                        locationName?.toLowerCase().includes('heathrow');
      
      setIsAirportDestination(isAirport);
      
      // Fetch airport details when destination is an airport
      if (isAirport && locationName) {
        fetchAirportDetails(locationName);
      } else {
        setDataAir(null); // Reset airport data if not airport
      }
    } else {
      setIsAirportDestination(false);
      setDataAir(null);
    }
  }, [locationFrom, locationTo, locations]);

  // Enhanced Custom Date Picker Component with React DatePicker
  const CustomDatePicker = ({ value, onChange, minDate, maxDate }) => {
    const [selectedDate, setSelectedDate] = useState(value || new Date());
    const [selectedTime, setSelectedTime] = useState(value || new Date());

    const handleDateChange = (date) => {
      if (date && !isNaN(date.getTime())) {
        const finalDate = new Date(date);
        finalDate.setHours(selectedTime.getHours(), selectedTime.getMinutes(), 0, 0);
        setSelectedDate(date);
        setSelectedTime(finalDate);
        onChange(finalDate);
      }
    };

    const handleTimeSelect = (time) => {
      const newDateTime = new Date(selectedDate);
      newDateTime.setHours(time.getHours(), time.getMinutes(), 0, 0);
      setSelectedTime(newDateTime);
      onChange(newDateTime);
    };

    const generateTimeSlots = () => {
      const slots = [];
      const startHour = 0;
      const endHour = 24;
      
      for (let hour = startHour; hour < endHour; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          const time = new Date(selectedDate);
          time.setHours(hour, minute, 0, 0);
          
          // Skip if time is in the past for today
          if (selectedDate.toDateString() === new Date().toDateString() && time < new Date()) {
            continue;
          }
          
          slots.push(time);
        }
      }
      return slots;
    };

    const formatTime = (date) => {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    };

    const scrollToSelectedTime = () => {
      setTimeout(() => {
        const selectedElement = document.querySelector('.time-slot-selected');
        if (selectedElement) {
          selectedElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'center'
          });
        }
      }, 100);
    };

    useEffect(() => {
      scrollToSelectedTime();
    }, [selectedTime]);

    return (
      <div className="custom-date-picker bg-gray-800 border border-gray-600 rounded-lg p-4 max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex flex-col lg:flex-row gap-6 flex-1 overflow-hidden">

         
        </div>

        {/* Selected Date Display */}
        <div className="mt-4 p-4 bg-gray-700 rounded-lg flex-shrink-0">
          <div className="text-sm text-gray-300 mb-2">Selected Date & Time</div>
          <div className="text-white font-semibold">
            {selectedDate.toLocaleDateString('en-US', {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </div>
          <div className="text-yellow-400 font-semibold text-lg">
            {selectedTime.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })}
          </div>
        </div>

       
      </div>
    );
  };

  const validateDateTimeSection = () => {
    const newErrors = {};
    if (!dateTime || isNaN(dateTime.getTime())) {
      newErrors.dateTime = "Valid date and time are required";
    } else if (dateTime < now) {
      newErrors.dateTime = "Date and time must be in the future";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePickupSection = () => {
    const newErrors = {};
    
    if (isAirportPickup) {
      if (!formData.arrivalTime) newErrors.arrivalTime = "Arrival time is required";
      if (!formData.flightNumber) newErrors.flightNumber = "Flight number is required";
      if (!formData.mobileNumber) newErrors.mobileNumber = "Mobile number is required";
    } else {
      if (!formData.sendhouseNo) newErrors.sendhouseNo = "House number is required";
      if (!formData.sendstreetName) newErrors.sendstreetName = "Street name is required";
      if (!formData.sendtown) newErrors.sendtown = "Town is required";
      if (!formData.sendpostCode) newErrors.sendpostCode = "Postal code is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateDestinationSection = () => {
    const newErrors = {};
    
    if (isAirportDestination) {
      // No validation needed for airport destination since data comes from API
    } else {
      if (!formData.rechouseNo) newErrors.rechouseNo = "House number is required";
      if (!formData.recstreetName) newErrors.recstreetName = "Street name is required";
      if (!formData.rectown) newErrors.rectown = "Town is required";
      if (!formData.recpostcode) newErrors.recpostcode = "Postal code is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePersonalSection = () => {
    const newErrors = {};
    if (!formData.title) newErrors.title = "Title is required";
    if (!formData.name) newErrors.name = "Full name is required";
    
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^[0-9+\- ]+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Please enter a valid phone number";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAllSections = () => {
    const dateTimeValid = validateDateTimeSection();
    const pickupValid = validatePickupSection();
    const destinationValid = validateDestinationSection();
    const personalValid = validatePersonalSection();

    if (!dateTimeValid) {
      setActiveSection("datetime");
      return false;
    }
    if (!pickupValid) {
      setActiveSection("pickup");
      return false;
    }
    if (!destinationValid) {
      setActiveSection("destination");
      return false;
    }
    if (!personalValid) {
      setActiveSection("personal");
      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleDateChange = (date) => {
    try {
      if (date && !isNaN(date.getTime())) {
        setDateTime(date);
        setErrors(prev => ({ ...prev, dateTime: undefined })); 
      } else {
        setErrors(prev => ({ ...prev, dateTime: "Invalid date selected" }));
      }
    } catch (error) {
      console.error("Date selection error:", error);
      setErrors(prev => ({ ...prev, dateTime: "Error selecting date" }));
    }
  };

 const handleBookNow = async () => {
    if (!validateAllSections()) {
      toast.error("Please fill in all required fields correctly");
      return;
    }

    try {
      const locationFromObj = locations.find(loc => loc.id.toString() === locationFrom);
      const locationToObj = locations.find(loc => loc.id.toString() === locationTo);

      if (!locationFromObj || !locationToObj) {
        throw new Error("Invalid locations selected");
      }

      // Create UK time date object
      const createUKDateTime = () => {
        // Assuming dateTime is a Date object from your datetime picker
        // We need to interpret it as UK time
        
        // Get the components from the selected datetime
        const year = dateTime.getFullYear();
        const month = dateTime.getMonth();
        const day = dateTime.getDate();
        const hours = dateTime.getHours();
        const minutes = dateTime.getMinutes();
        
        // Create a new date as if it's in UK timezone
        // Method 1: Create UTC date with UK time components
        const ukDate = new Date(Date.UTC(year, month, day, hours, minutes));
        
        // OR Method 2: Send as string without timezone
        // Format: "2025-12-21T17:30:00" (no 'Z' at the end)
        const ukDateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
        
        return ukDateString; // Send as string without timezone
      };

      const bookingPayload = {
        title: formData.title || "",
        name: formData.name || "",
        price: pricedetail || 0,
        email: formData.email || "",
        phoneNumber: formData.phoneNumber || "",
        locationFrom: locationFromObj,
        locationTo: locationToObj,
        postalCodeFrom: formData.sendpostCode || "",
        postalCodeTo: formData.recpostcode || "",
        streetFrom: formData.sendstreetName || "",
        streetTo: formData.recstreetName || "",
        // FIXED: Don't use toISOString()
        dateBooked: createUKDateTime(), // Send as UK time string
        instructions: formData.instructions || "",
        houseNoTo: formData.rechouseNo || "",
        houseNoFrom: formData.sendhouseNo || "",
        townFrom: formData.sendtown || "",
        townTo: formData.rectown || "",
        vehicle: bookingData.selectedVehicle || "",
        arrivalTime: formData.arrivalTime || "",
        flightNumber: formData.flightNumber || "",
        mobileNumber: formData.mobileNumber || "",
        departureTime: formData.departureTime || "",
        destinationFlightNumber: formData.destinationFlightNumber || "",
        terminal: formData.terminal || "",
        priceDetails: bookingData.priceDetails || {},
        airportData: dataAir || {}
      };

      // Debug: Log what we're sending
      console.log('=== BOOKING DEBUG ===');
      console.log('Original dateTime:', dateTime);
      console.log('Original toISOString:', dateTime.toISOString());
      console.log('Sending as UK time:', createUKDateTime());
      console.log('Booking payload:', bookingPayload);

      const response = await fetch(`${BASE_URL}/api/bookings/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bookingPayload),
      });

      const data = await response.json();
      if (response.ok) {
        onClose();
        window.location.href = `/details/${data?.booking?._id}`;
        
        if (typeof onBookingSuccess === 'function') {
          onBookingSuccess();
        }
        
        toast.success("Booking successful!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          closeButton: true,
        });
      }
    } catch(err) {
      toast.error("Booking failed. Please try again.");
    }
  };

  return (
    <div className="bg-gray-1200 rounded-lg text-white w-full max-w-md md:max-w-2xl lg:max-w-4xl mx-auto">
      {/* Modal Header */}
      <div className="border-b border-gray-700 px-8 py-4 flex justify-between items-center">
        <h3 className="text-xl font-bold">Complete Your Booking</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white focus:outline-none"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Progress Steps */}
      <div className="px-6 py-4 border-b border-gray-700">
        <div className="flex justify-between items-center">
          <button
            className={`flex flex-col items-center ${activeSection === "datetime" ? "text-yellow-400" : "text-gray-400"}`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeSection === "datetime" ? "bg-yellow-500/10 border-2 border-yellow-400" : "bg-gray-700"}`}>
              1
            </div>
            <span className="mt-2 text-sm">Date & Time</span>
          </button>

          <div className={`flex-1 h-1 mx-2 ${activeSection !== "datetime" ? "bg-yellow-400" : "bg-gray-600"}`}></div>

          <button
            onClick={() => setActiveSection("pickup")}
            className={`flex flex-col items-center ${activeSection === "pickup" ? "text-yellow-400" : "text-gray-400"}`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeSection === "pickup" ? "bg-yellow-500/10 border-2 border-yellow-400" : "bg-gray-700"}`}>
              2
            </div>
            <span className="mt-2 text-sm">Pickup</span>
          </button>

          <div className={`flex-1 h-1 mx-2 ${activeSection === "destination" || activeSection === "personal" ? "bg-yellow-400" : "bg-gray-600"}`}></div>

          <button
            onClick={() => setActiveSection("destination")}
            className={`flex flex-col items-center ${activeSection === "destination" ? "text-yellow-400" : "text-gray-400"}`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeSection === "destination" ? "bg-yellow-500/10 border-2 border-yellow-400" : "bg-gray-700"}`}>
              3
            </div>
            <span className="mt-2 text-sm">Destination</span>
          </button>

          <div className={`flex-1 h-1 mx-2 ${activeSection === "personal" ? "bg-yellow-400" : "bg-gray-600"}`}></div>

          <button
            onClick={() => setActiveSection("personal")}
            className={`flex flex-col items-center ${activeSection === "personal" ? "text-yellow-400" : "text-gray-400"}`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activeSection === "personal" ? "bg-yellow-500/10 border-2 border-yellow-400" : "bg-gray-700"}`}>
              4
            </div>
            <span className="mt-2 text-sm">Personal Info</span>
          </button>
        </div>
      </div>

      {/* Form Content */}
      <div className="p-6">
        {/* Date & Time Section */}
        {(activeSection === "datetime") && (
          <div className="space-y-6">
           
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Date & Time</label>
                <div className="relative w-full">
                  <CustomDatePicker
                    value={dateTime}
                    onChange={handleDateChange}
                    minDate={now}
                    maxDate={new Date(now.getFullYear() + 1, now.getMonth(), now.getDate())}
                  />
                  {errors.dateTime && (
                    <p className="mt-1 text-sm text-red-400">{errors.dateTime}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  if (validateDateTimeSection()) {
                    setActiveSection("pickup");
                  }
                }}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 rounded-lg font-medium"
              >
                Next: Pickup Details
              </button>
            </div>
          </div>
        )}

        {/* Pickup Section */}
        {(activeSection === "pickup") && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Pickup Details</h2>
            <div className="space-y-4">
             <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Journey From</label>
            <div className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white">
              {bookingData?.locationFrom?.name}
            </div>
          </div>
            
              {!isAirportPickup && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">House Number/Name</label>
                      <input
                        type="text"
                        value={formData.sendhouseNo}
                        onChange={handleChange}
                        name="sendhouseNo"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                      />
                      {errors.sendhouseNo && (
                        <p className="mt-1 text-sm text-red-400">{errors.sendhouseNo}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Street Name</label>
                      <input
                        type="text"
                        value={formData.sendstreetName}
                        onChange={handleChange}
                        name="sendstreetName"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                      />
                      {errors.sendstreetName && (
                        <p className="mt-1 text-sm text-red-400">{errors.sendstreetName}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Town</label>
                      <input
                        type="text"
                        value={formData.sendtown}
                        onChange={handleChange}
                        name="sendtown"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                      />
                      {errors.sendtown && (
                        <p className="mt-1 text-sm text-red-400">{errors.sendtown}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Postal Code</label>
                      <input
                        type="text"
                        value={formData.sendpostCode}
                        onChange={handleChange}
                        name="sendpostCode"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                      />
                      {errors.sendpostCode && (
                        <p className="mt-1 text-sm text-red-400">{errors.sendpostCode}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {isAirportPickup && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Expected Time Of Arrival
                    </label>
                    <input
                      type="time"
                      value={formData.arrivalTime}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          arrivalTime: e.target.value
                        }));
                      }}
                      name="arrivalTime"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                      required
                      step="300"
                    />
                    {errors.arrivalTime && (
                      <p className="mt-1 text-sm text-red-400">{errors.arrivalTime}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Flight Number
                    </label>
                    <input
                      type="text"
                      value={formData.flightNumber}
                      onChange={handleChange}
                      name="flightNumber"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                      placeholder="e.g. BA123"
                      required
                    />
                    {errors.flightNumber && (
                      <p className="mt-1 text-sm text-red-400">{errors.flightNumber}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      value={formData.mobileNumber}
                      onChange={handleChange}
                      name="mobileNumber"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                      placeholder="For flight status updates"
                      required
                    />
                    {errors.mobileNumber && (
                      <p className="mt-1 text-sm text-red-400">{errors.mobileNumber}</p>
                    )}
                  </div>
                 {/* <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                  <p className="block text-sm font-medium text-blue-800 mb-1">
                    Car park charge will be added at cost to fare
                  </p>
                </div> */}
                </div>
              )}
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => setActiveSection("datetime")}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium"
              >
                Back
              </button>
              <button
                onClick={() => {
                  if (validatePickupSection()) {
                    setActiveSection("destination");
                  }
                }}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 rounded-lg font-medium"
              >
                Next: Destination Details
              </button>
            </div>
          </div>
        )}

        {/* Destination Section */}
        {(activeSection === "destination") && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Destination Details</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Journey To</label>
                <div className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white">
                  {bookingData?.locationTo?.name}
                </div>
              </div>

              {!isAirportDestination && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">House Number/Name</label>
                      <input
                        type="text"
                        value={formData.rechouseNo}
                        onChange={handleChange}
                        name="rechouseNo"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                      />
                      {errors.rechouseNo && (
                        <p className="mt-1 text-sm text-red-400">{errors.rechouseNo}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Street Name</label>
                      <input
                        type="text"
                        value={formData.recstreetName}
                        onChange={handleChange}
                        name="recstreetName"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                      />
                      {errors.recstreetName && (
                        <p className="mt-1 text-sm text-red-400">{errors.recstreetName}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Town</label>
                      <input
                        type="text"
                        value={formData.rectown}
                        onChange={handleChange}
                        name="rectown"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                      />
                      {errors.rectown && (
                        <p className="mt-1 text-sm text-red-400">{errors.rectown}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">Postal Code</label>
                      <input
                        type="text"
                        value={formData.recpostcode}
                        onChange={handleChange}
                        name="recpostcode"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                      />
                      {errors.recpostcode && (
                        <p className="mt-1 text-sm text-red-400">{errors.recpostcode}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {isAirportDestination && (
                <div className="space-y-4">
                  {isLoadingAirportData ? (
                    <div className="text-center py-4">
                      <div className="text-yellow-400">Loading airport details...</div>
                    </div>
                  ) : dataAir ? (
                    <div className="bg-gray-700 border border-gray-600 rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-yellow-400 mb-3">Airport Details</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-300">Street</label>
                          <div className="text-white bg-gray-600 px-3 py-2 rounded mt-1">
                            {formData.streetName1 || "Not specified"}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300">Postcode</label>
                          <div className="text-white bg-gray-600 px-3 py-2 rounded mt-1">
                            {formData.post_code || "Not specified"}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300">Town</label>
                          <div className="text-white bg-gray-600 px-3 py-2 rounded mt-1">
                            {formData.town || "Not specified"}
                          </div>
                        </div>
                      </div>
                     
                    </div>
                  ) : (
                    <div className="text-center py-4 text-red-400">
                      Unable to load airport details. Please try again.
                    </div>
                  )}
                  {errors.airportData && (
                    <p className="text-sm text-red-400">{errors.airportData}</p>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => setActiveSection("pickup")}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium"
              >
                Back
              </button>
              <button
                onClick={() => {
                  if (validateDestinationSection()) {
                    setActiveSection("personal");
                  }
                }}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 rounded-lg font-medium"
              >
                Next: Personal Information
              </button>
            </div>
          </div>
        )}

        {/* Personal Information Section */}
        {(activeSection === "personal") && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold">Personal Information</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
                  <select
                    value={formData.title}
                    onChange={handleChange}
                    name="title"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                  >
                    <option value="">Select Title</option>
                    <option value="Mr">Mr</option>
                    <option value="Miss">Miss</option>
                    <option value="Ms">Ms</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Prof">Prof</option>
                    <option value="Dr">Dr</option>
                    <option value="Sir">Sir</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    name="name"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                    required
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-400">{errors.name}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Email Address</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  name="email"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                  required
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  name="phoneNumber"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                  required
                  pattern="[0-9]{11}"
                  inputMode="numeric"
                  minLength={11}
                />
                {errors.phoneNumber && (
                  <p className="mt-1 text-sm text-red-400">{errors.phoneNumber}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Special Instructions (Optional)</label>
                <textarea
                  value={formData.instructions}
                  onChange={handleChange}
                  name="instructions"
                  rows="3"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                ></textarea>
              </div>
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => setActiveSection("destination")}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium"
              >
                Back
              </button>
              <button
                onClick={handleBookNow}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold"
              >
                Confirm Booking
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Price Summary (Fixed at bottom) */}
      <div className="border-t border-gray-700 p-4 bg-gray-800/50">
        <div className="flex justify-between items-center">
          <div>
            <h4 className="font-medium">{bookingData?.selectedVehicle?.title}</h4>
            <p className="text-sm text-gray-400">
              {bookingData?.locationFrom?.name} → {bookingData?.locationTo?.name}
            </p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-yellow-400">
              £{bookingData?.priceDetails?.total?.toFixed(2) || "0.00"}
            </p>
            <p className="text-xs text-gray-400">Total Price</p>
          </div>
        </div>
      </div>

      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default DetailedForm;